/**
 * useBoostSignals — infer paid boosting from post engagement patterns.
 *
 * Signal: views / followers ratio. A post that reaches >50% of a page's
 * followers in views is almost certainly boosted (organic IG reach caps
 * around 15-20% for most accounts). Bigger accounts (>100k followers)
 * see even lower organic ratios, so the same 50% threshold is very safe.
 *
 * Empirically calibrated 2026-07-11 across 7 tracked competitors + Flax:
 * - Boojee: avg 181%, 13/16 posts likely boosted (heavy pusher)
 * - Harvest: avg 112%, Veronica's: avg 106% (heavy)
 * - Sweetgreen: avg 6.6%, Flax: avg 6.4% (organic)
 *
 * Reads existing marketing.ig_competitor_posts + latest ig_competitor_snapshot.
 * No new sync, no new schema.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export type BoostTier = 'likely' | 'possible' | 'organic' | 'unknown';

export function tierForScore(score: number | null): BoostTier {
  if (score == null || !Number.isFinite(score)) return 'unknown';
  if (score >= 50) return 'likely';
  if (score >= 20) return 'possible';
  return 'organic';
}

export interface BoostSignal {
  username: string;
  postsWithViews: number;
  avgScore: number;
  maxScore: number;
  likelyBoosted: number;
  possibleBoost: number;
  organic: number;
  topBoost:
    | {
        postId: string;
        views: number;
        followers: number;
        score: number;
        thumbnailUrl: string | null;
        imageUrl: string | null;
        r2ThumbKey: string | null;
        r2MediaKey: string | null;
        mediaType: string | null;
        permalink: string | null;
        caption: string | null;
        postedAt: string | null;
        likes: number | null;
        comments: number | null;
      }
    | null;
}

interface RawPost {
  username: string;
  post_id: string;
  views: number | null;
  like_count: number | null;
  comments_count: number | null;
  media_type: string | null;
  posted_at: string | null;
  caption: string | null;
  permalink: string | null;
  thumbnail_url: string | null;
  image_url: string | null;
  r2_thumb_key: string | null;
  r2_media_key: string | null;
}

interface RawSnapshot {
  username: string;
  followers_count: number | null;
  snapshot_date: string;
}

export function useBoostSignals(daysWindow = 30) {
  const posts = useQuery({
    queryKey: ['boost-signals-posts', daysWindow],
    queryFn: async (): Promise<RawPost[]> => {
      const since = new Date(Date.now() - daysWindow * 86400000).toISOString();
      const { data, error } = await marketing
        .from('ig_competitor_posts')
        .select(
          'username, post_id, views, like_count, comments_count, media_type, posted_at, caption, permalink, thumbnail_url, image_url, r2_thumb_key, r2_media_key',
        )
        .gte('posted_at', since)
        .order('posted_at', { ascending: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as unknown as RawPost[];
    },
    staleTime: 60_000,
  });

  const snaps = useQuery({
    queryKey: ['boost-signals-snapshots'],
    queryFn: async (): Promise<RawSnapshot[]> => {
      const { data, error } = await marketing
        .from('ig_competitor_snapshot')
        .select('username, followers_count, snapshot_date')
        .order('snapshot_date', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as RawSnapshot[];
    },
    staleTime: 5 * 60_000,
  });

  const signals = useMemo<BoostSignal[]>(() => {
    if (!posts.data || !snaps.data) return [];
    const followersByHandle = new Map<string, number>();
    for (const s of snaps.data) {
      if (followersByHandle.has(s.username)) continue;
      if (s.followers_count != null && s.followers_count > 0) {
        followersByHandle.set(s.username, s.followers_count);
      }
    }
    const byUser = new Map<string, RawPost[]>();
    for (const p of posts.data) {
      if (!p.views || p.views <= 0) continue;
      const arr = byUser.get(p.username) ?? [];
      arr.push(p);
      byUser.set(p.username, arr);
    }

    const out: BoostSignal[] = [];
    for (const [username, list] of byUser) {
      const followers = followersByHandle.get(username);
      if (!followers) continue;
      let sum = 0;
      let max = 0;
      let likely = 0;
      let possible = 0;
      let organic = 0;
      let top: BoostSignal['topBoost'] = null;
      for (const p of list) {
        const score = (p.views! / followers) * 100;
        sum += score;
        if (score > max) {
          max = score;
          top = {
            postId: p.post_id,
            views: p.views!,
            followers,
            score,
            thumbnailUrl: p.thumbnail_url,
            imageUrl: p.image_url,
            r2ThumbKey: p.r2_thumb_key,
            r2MediaKey: p.r2_media_key,
            mediaType: p.media_type,
            permalink: p.permalink,
            caption: p.caption,
            postedAt: p.posted_at,
            likes: p.like_count,
            comments: p.comments_count,
          };
        }
        const tier = tierForScore(score);
        if (tier === 'likely') likely++;
        else if (tier === 'possible') possible++;
        else if (tier === 'organic') organic++;
      }
      out.push({
        username,
        postsWithViews: list.length,
        avgScore: sum / list.length,
        maxScore: max,
        likelyBoosted: likely,
        possibleBoost: possible,
        organic,
        topBoost: top,
      });
    }
    return out.sort((a, b) => b.avgScore - a.avgScore);
  }, [posts.data, snaps.data]);

  return { signals, isLoading: posts.isLoading || snaps.isLoading };
}

export function useBoostScoresForHandle(username: string | null, daysWindow = 30) {
  const posts = useQuery({
    queryKey: ['boost-scores-posts', username, daysWindow],
    enabled: !!username,
    queryFn: async (): Promise<RawPost[]> => {
      if (!username) return [];
      const since = new Date(Date.now() - daysWindow * 86400000).toISOString();
      const { data, error } = await marketing
        .from('ig_competitor_posts')
        .select(
          'username, post_id, views, like_count, comments_count, media_type, posted_at, caption, permalink, thumbnail_url, image_url, r2_thumb_key, r2_media_key',
        )
        .eq('username', username)
        .gte('posted_at', since)
        .order('posted_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as unknown as RawPost[];
    },
    staleTime: 60_000,
  });

  const snap = useQuery({
    queryKey: ['boost-scores-snap', username],
    enabled: !!username,
    queryFn: async (): Promise<number | null> => {
      if (!username) return null;
      // Latest snapshot may have null followers (e.g. first sync failed
      // to hydrate the profile). Walk back until we find one with a real
      // count so per-brand tabs behave like the summary aggregate.
      const { data, error } = await marketing
        .from('ig_competitor_snapshot')
        .select('followers_count')
        .eq('username', username)
        .not('followers_count', 'is', null)
        .gt('followers_count', 0)
        .order('snapshot_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as { followers_count: number | null } | null)?.followers_count ?? null;
    },
    staleTime: 5 * 60_000,
  });

  const scored = useMemo(() => {
    if (!posts.data) return [];
    const followers = snap.data ?? 0;
    return posts.data.map((p) => {
      const score = p.views && followers > 0 ? (p.views / followers) * 100 : null;
      return { ...p, boostScore: score, tier: tierForScore(score) };
    });
  }, [posts.data, snap.data]);

  return { posts: scored, followers: snap.data ?? null, isLoading: posts.isLoading || snap.isLoading };
}
